// src/components/dashboard/FeedbackAnalyticsPanel.tsx

import React, { useState, useEffect } from 'react';
import { MessageCircle, ArrowUpCircle, ArrowDownCircle, Minus, HelpCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useError } from '../../context/ErrorContext';
import { useColorMode } from '../../context/ColorModeContext';
// Re-cableado a lealify-base (/analytics/feedback-timeline) — reemplaza Elasticsearch.
import { analyticsApi } from '../../api/analyticsApi';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar, Cell } from 'recharts';
import CountUp from '../ui/CountUp';

// Define los tipos para los datos
interface FeedbackMetrics {
  overall: {
    totalFeedback: number;
    distribution: {
      type: string;
      count: number;
      percent: number;
    }[];
    npsScore: number;
    averageScore: number;
  };
  timeline: {
    period: string;
    totalFeedback: number;
    distribution: {
      [key: string]: {
        count: number;
        percent: number;
      };
    };
    averageScore: number;
  }[];
}

const FeedbackAnalyticsPanel: React.FC = () => {
  const { currentUser } = useAuth();
  const { addError, clearErrors } = useError();
  const { mode } = useColorMode();
  const isDark = mode === 'dark';
  const [metrics, setMetrics] = useState<FeedbackMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [interval, setInterval] = useState<'day' | 'week' | 'month'>('week');
  const [dateRange] = useState<{ start: Date; end: Date }>({
    start: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), // 90 días atrás
    end: new Date()
  });

  // Función para cargar los datos
  const loadFeedbackMetrics = async () => {
    if (!currentUser?.businessId) return;
    
    setLoading(true);
    clearErrors();
    
    try {
      const data = await analyticsApi.feedbackByPeriod(
        currentUser.businessId,
        interval,
        dateRange.start,
        dateRange.end
      );
      
      setMetrics(data);
    } catch (error) {
      console.error('Error loading feedback metrics:', error);
      addError('No se pudieron cargar las métricas de satisfacción', 'data', 'feedback-metrics');
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos al iniciar o cambiar parámetros
  useEffect(() => {
    loadFeedbackMetrics();
  }, [currentUser?.businessId, interval]);

  // Helper para formatear porcentajes
  const formatPercent = (value: number) => `${value.toFixed(1)}%`;

  // Helper para obtener colores por tipo de feedback.
  // En oscuro bajamos los tonos -400 (deslumbran/fosforescentes sobre fondo dark)
  // a tonos -700 apagados. En claro se mantienen idénticos.
  const getFeedbackColor = (type: string) => {
    switch (type) {
      case 'excelente': return isDark ? '#B45309' : '#FBBF24'; // amber-700 / yellow-400
      case 'bueno':     return isDark ? '#047857' : '#34D399'; // emerald-700 / emerald-400
      case 'neutral':   return isDark ? '#1D4ED8' : '#60A5FA'; // blue-700 / blue-400
      case 'malo':      return isDark ? '#B91C1C' : '#F87171'; // red-700 / red-400
      case 'omitted':   return isDark ? '#4B5563' : '#9CA3AF'; // gray-600 / gray-400
      default:          return isDark ? '#4B5563' : '#9CA3AF'; // gray-600 / gray-400
    }
  };
  // Opacidad de relleno de las áreas: en oscuro las bajamos para que no
  // "quemen" sobre el fondo (el trazo del borde sigue definiendo cada serie).
  const areaFillOpacity = isDark ? 0.35 : 0.6;

  // Helper para obtener icono por tipo de feedback
  const getFeedbackIcon = (type: string) => {
    switch (type) {
      case 'excelente': return <ArrowUpCircle className="h-4 w-4 text-yellow-500" />;
      case 'bueno': return <ArrowUpCircle className="h-4 w-4 text-green-500" />;
      case 'neutral': return <Minus className="h-4 w-4 text-blue-500" />;
      case 'malo': return <ArrowDownCircle className="h-4 w-4 text-red-500" />;
      case 'omitted': return <HelpCircle className="h-4 w-4 text-content-3" />;
      default: return <HelpCircle className="h-4 w-4 text-content-3" />;
    }
  };

  // Formatear datos para gráficos
  const prepareChartData = () => {
    if (!metrics?.timeline) return [];
    
    return metrics.timeline.map(item => {
      // Crear un objeto que contenga todas las propiedades necesarias para el gráfico
      const result: any = { period: item.period };
      
      // Añadir porcentajes de cada tipo de feedback
      ['excelente', 'bueno', 'neutral', 'malo', 'omitted'].forEach(type => {
        result[type] = item.distribution[type]?.percent || 0;
      });
      
      // Añadir puntuación media
      result.averageScore = item.averageScore;
      
      return result;
    });
  };

  // Preparar datos para el gráfico de distribución general
  const prepareOverallData = () => {
    if (!metrics?.overall?.distribution) return [];
    
    return metrics.overall.distribution.map(item => ({
      name: item.type === 'omitted' ? 'Sin calificar' : 
            item.type === 'excelente' ? 'Excelente' :
            item.type === 'bueno' ? 'Bueno' :
            item.type === 'neutral' ? 'Neutral' : 'Malo',
      value: item.percent,
      count: item.count,
      color: getFeedbackColor(item.type),
      type: item.type
    }));
  };

  const chartData = prepareChartData();
  const overallData = prepareOverallData();

  return (
    <div className="bg-surface shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
        <h3 className="text-lg leading-6 font-medium text-content flex items-center">
          <MessageCircle className="h-5 w-5 mr-2 text-indigo-500" /> 
          Análisis de Satisfacción
        </h3>
        
        <div className="flex space-x-2">
          {/* Selector de intervalo */}
          <select 
            value={interval}
            onChange={(e) => setInterval(e.target.value as any)}
            className="text-sm border-line rounded-md shadow-sm focus:ring-accent focus:border-accent"
          >
            <option value="day">Diario</option>
            <option value="week">Semanal</option>
            <option value="month">Mensual</option>
          </select>
          
          {/* Botón para recargar */}
          <button 
            onClick={() => loadFeedbackMetrics()} 
            disabled={loading}
            className="inline-flex items-center px-2.5 py-1.5 border border-line shadow-sm text-xs font-medium rounded text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Cargando...' : 'Actualizar'}
          </button>
        </div>
      </div>
      
      <div className="border-t border-line">
        {loading ? (
          <div className="px-4 py-16 sm:px-6 text-center text-content-3">
            <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin text-indigo-500" />
            <p>Cargando datos de satisfacción...</p>
          </div>
        ) : !metrics ? (
          <div className="px-4 py-16 sm:px-6 text-center text-content-3">
            <p>No hay datos disponibles o el endpoint aún no está implementado.</p>
            <p className="mt-2 text-sm">
              <button 
                onClick={loadFeedbackMetrics}
                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:text-indigo-300 font-medium"
              >
                Intentar nuevamente
              </button>
            </p>
          </div>
        ) : (
          <div className="px-4 py-5 sm:p-6">
            {/* Resumen de NPS y promedio */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-indigo-50 dark:bg-indigo-500/10 rounded-lg p-4">
                <h4 className="text-sm font-medium text-indigo-800 dark:text-indigo-300 mb-2">Puntuación de Satisfacción (NPS)</h4>
                <div className="flex items-center">
                  <span className={`text-3xl font-display font-bold ${metrics.overall.npsScore >= 0 ? 'text-green-600 dark:text-green-500' : 'text-red-600 dark:text-red-500'}`}>
                    <CountUp value={metrics.overall.npsScore} format={(n) => n.toFixed(1)} />
                  </span>
                  <span className="text-sm ml-2 text-content-3">(-100 a 100)</span>
                </div>
                <p className="text-xs text-content-3 mt-1">
                  Basado en {metrics.overall.totalFeedback} valoraciones
                </p>
              </div>
              
              <div className="bg-yellow-50 dark:bg-yellow-500/10 rounded-lg p-4">
                <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-2">Calificación Promedio</h4>
                <div className="flex items-center">
                  <span className="text-3xl font-display font-bold text-yellow-600 dark:text-yellow-500">
                    <CountUp value={metrics.overall.averageScore} format={(n) => n.toFixed(1)} />
                  </span>
                  <span className="text-sm ml-2 text-content-3">(0 a 5)</span>
                </div>
                <p className="text-xs text-content-3 mt-1">
                  Promedio ponderado de todas las valoraciones
                </p>
              </div>
            </div>
            
            {/* Distribución general de feedback */}
            <div className="mb-8">
              <h4 className="text-sm font-medium text-content-2 mb-4">Distribución de Valoraciones</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={overallData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={formatPercent} />
                    <YAxis dataKey="name" type="category" width={100} />
                    <Tooltip 
                      formatter={(value: any) => [`${value.toFixed(1)}% (${overallData.find(d => d.value === value)?.count || 0})`, 'Porcentaje']}
                    />
                    <Bar 
                      dataKey="value" 
                      fill="#8884d8"
                      background={{ fill: isDark ? '#374151' : '#eee' }}
                      radius={[0, 4, 4, 0]}
                    >
                      {overallData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Gráfico de tendencia por período */}
            <div>
              <h4 className="text-sm font-medium text-content-2 mb-4">Tendencia de Satisfacción por Período</h4>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis tickFormatter={formatPercent} />
                    <Tooltip formatter={(value: any) => [`${value.toFixed(1)}%`, '']} />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="excelente" 
                      stackId="1"
                      name="Excelente" 
                      stroke={getFeedbackColor('excelente')} 
                      fill={getFeedbackColor('excelente')} fillOpacity={areaFillOpacity} 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="bueno" 
                      stackId="1"
                      name="Bueno" 
                      stroke={getFeedbackColor('bueno')} 
                      fill={getFeedbackColor('bueno')} fillOpacity={areaFillOpacity} 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="neutral" 
                      stackId="1"
                      name="Neutral" 
                      stroke={getFeedbackColor('neutral')} 
                      fill={getFeedbackColor('neutral')} fillOpacity={areaFillOpacity} 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="malo" 
                      stackId="1"
                      name="Malo" 
                      stroke={getFeedbackColor('malo')} 
                      fill={getFeedbackColor('malo')} fillOpacity={areaFillOpacity} 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="omitted" 
                      stackId="1"
                      name="Sin calificar" 
                      stroke={getFeedbackColor('omitted')} 
                      fill={getFeedbackColor('omitted')} fillOpacity={areaFillOpacity} 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Tabla resumen de feedback por período */}
            {metrics.timeline.length > 0 && (
              <div className="mt-8">
                <h4 className="text-sm font-medium text-content-2 mb-4">Resumen por Período</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-line">
                    <thead className="bg-surface-2">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                          Período
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                          Total
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                          Promedio
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                          Excelente
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                          Bueno
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                          Neutral
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                          Malo
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-surface divide-y divide-line">
                      {metrics.timeline.map((period, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-surface' : 'bg-surface-2'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-content">
                            {period.period}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-content-3">
                            {period.totalFeedback}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-content-3">
                            {period.averageScore ? period.averageScore.toFixed(1) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-yellow-500 text-sm mr-1">{getFeedbackIcon('excelente')}</span>
                              <span className="text-sm text-content-3">
                                {period.distribution.excelente 
                                  ? `${period.distribution.excelente.count} (${period.distribution.excelente.percent.toFixed(1)}%)` 
                                  : '0 (0%)'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-green-500 text-sm mr-1">{getFeedbackIcon('bueno')}</span>
                              <span className="text-sm text-content-3">
                                {period.distribution.bueno 
                                  ? `${period.distribution.bueno.count} (${period.distribution.bueno.percent.toFixed(1)}%)` 
                                  : '0 (0%)'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-blue-500 text-sm mr-1">{getFeedbackIcon('neutral')}</span>
                              <span className="text-sm text-content-3">
                                {period.distribution.neutral 
                                  ? `${period.distribution.neutral.count} (${period.distribution.neutral.percent.toFixed(1)}%)` 
                                  : '0 (0%)'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-red-500 text-sm mr-1">{getFeedbackIcon('malo')}</span>
                              <span className="text-sm text-content-3">
                                {period.distribution.malo 
                                  ? `${period.distribution.malo.count} (${period.distribution.malo.percent.toFixed(1)}%)` 
                                  : '0 (0%)'}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackAnalyticsPanel;